import logInDevMode from 'logInDevMode';
import { storageSpaceAvailable } from 'storageSpaceAvailable';
import { type CurrentUserIguanaObject } from 'Users';
import { type FrontRoyalStore, type PublishedStream } from 'FrontRoyalStore';
import { type EventLogger } from 'EventLogger';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import type TimerSingleton from 'FrontRoyalTimer/TimerSingleton';
import { generateGuid } from 'guid';
import throwIfNotEnoughStorageAvailable from './throwIfNotEnoughStorageAvailable';
import StorableImageError from './StorableImageError';
import { getStreamAndEnsureStored } from './getStreamAndEnsureStored';

// How many of the highest priority streams we keep available offline. Native memory retention
// scales with the volume a session downloads, so a session that walks the whole curriculum
// reaches the iOS jetsam limit.
const STREAM_WINDOW_SIZE = 10;

// A stream that always fails would otherwise be re-downloaded on every pass for the rest of the
// session, which costs battery and delays the streams after it in the pass. Giving up stops that
// work.
const MAX_ATTEMPTS_PER_STREAM = 3;
const failedAttemptsByStreamId = new Map<string, number>();

// 408 and 429 are the two 4xx codes that invite us back.
function assetIsGoneForGood(status: number | undefined) {
    if (status === undefined) return false;
    return status >= 400 && status < 500 && ![408, 429].includes(status);
}

function recordFailedAttempt({
    id,
    err,
    injector,
    executionId,
}: {
    id: string;
    err: StorableImageError;
    injector: ng.auto.IInjectorService;
    executionId: string;
}) {
    const failedAttempts = (failedAttemptsByStreamId.get(id) ?? 0) + 1;
    const assetIsGone = assetIsGoneForGood(err.status);
    const giveUp = assetIsGone || failedAttempts >= MAX_ATTEMPTS_PER_STREAM;

    failedAttemptsByStreamId.set(id, giveUp ? MAX_ATTEMPTS_PER_STREAM : failedAttempts);
    if (!giveUp) return;

    injector
        .get<typeof ErrorLogServiceClass>('ErrorLogService')
        .notify('Skipped a stream during the offline download', err, {
            stream_id: id,
            execution_id: executionId,
            failed_attempts: failedAttempts,
            image_status: err.status ?? null,
        });
}

function streamIsAlreadyLoaded(stream: PublishedStream, currentUserLocale: string) {
    if (!stream.all_content_stored) return false;
    if (stream.locale === currentUserLocale) return true;

    // If the user has selected a locale other than English, the stream may not be available
    // in their locale. If the stream is not available in their locale, then we expect to have
    // the English version loaded.
    const streamHasTranslationInCurrentUserLocale = stream.locale_pack.content_items.find(
        ci => ci.locale === currentUserLocale,
    );
    if (!streamHasTranslationInCurrentUserLocale && stream.locale === 'en') return true;

    // If we get here, it means that we have a fully stored version of the stream in the database,
    // but it is not for the correct locale. So, we need to reload the stream in the appropriate locale.
    return false;
}

async function logStart({
    injector,
    streamIdsToFetch,
    streamsThatShouldBeLoaded,
    streamLocalePackIds,
    executionId,
}: {
    injector: ng.auto.IInjectorService;
    streamIdsToFetch: string[];
    streamsThatShouldBeLoaded: PublishedStream[];
    streamLocalePackIds: string[];
    executionId: string;
}) {
    logInDevMode(
        injector,
        `loading ${streamIdsToFetch.length} of the ${STREAM_WINDOW_SIZE} highest priority streams ` +
            `(${streamLocalePackIds.length} in the curriculum)`,
    );
    const availableSpace = await storageSpaceAvailable();
    injector.get<EventLogger>('EventLogger').log('offline_mode:loading_streams', {
        label: executionId,
        total: streamsThatShouldBeLoaded.length,
        value: streamIdsToFetch.length,
        storage_space_available: availableSpace,

        // see https://trello.com/c/iLC4Bz4p
        params: {
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
        },
    });
}

async function getStreamsToLoad({
    injector,
    streamLocalePackIds,
    currentUser,
}: {
    injector: ng.auto.IInjectorService;
    streamLocalePackIds: string[];
    currentUser: CurrentUserIguanaObject;
}) {
    const frontRoyalStore = injector.get<FrontRoyalStore>('frontRoyalStore');

    const streamsThatShouldBeLoaded = (
        await frontRoyalStore.retryOnHandledError(db =>
            db.publishedStreams.where('locale_pack.id').anyOf(streamLocalePackIds).toArray(),
        )
    ).sort((a, b) =>
        streamLocalePackIds.indexOf(a.locale_pack.id) < streamLocalePackIds.indexOf(b.locale_pack.id) ? -1 : 1,
    );

    // Take the window before we drop the stored streams, not after. A stored stream holds its
    // slot, so the pass has nothing to do once the window is full. Slicing after the filter
    // would instead pull the next unstored stream forward every pass, and a long enough session
    // would download the whole curriculum.
    const streamIdsToFetch = streamsThatShouldBeLoaded
        .slice(0, STREAM_WINDOW_SIZE)
        .filter(stream => !streamIsAlreadyLoaded(stream, currentUser.pref_locale))
        .map(s => s.id);

    return { streamsThatShouldBeLoaded, streamIdsToFetch };
}

async function execute(
    streamLocalePackIds: string[],
    currentUser: CurrentUserIguanaObject,
    injector: ng.auto.IInjectorService,
    executionId: string,
    abortLoadingPromise: Promise<void>,
) {
    let abortLoading = false;
    abortLoadingPromise.then(() => {
        abortLoading = true;
    });

    let { streamsThatShouldBeLoaded, streamIdsToFetch } = await getStreamsToLoad({
        currentUser,
        injector,
        streamLocalePackIds,
    });

    streamIdsToFetch = streamIdsToFetch.filter(id => (failedAttemptsByStreamId.get(id) ?? 0) < MAX_ATTEMPTS_PER_STREAM);

    await logStart({ injector, streamIdsToFetch, streamsThatShouldBeLoaded, streamLocalePackIds, executionId });

    for (let i = 0; i < streamIdsToFetch.length; i += 1) {
        const id = streamIdsToFetch[i];

        // FIXME:
        // There are 2 reasons we use a for loop with an await inside of it instead of
        // calling fetchAndStoreStream once for each stream and awaiting with Promise.all:
        // 1. We don't want to send out all those api requests together, since they'll lock
        //      up the queue (this wouldn't be an issue if we stopped queuing the requests,
        //      but right now that also means we would lose retry logic and, since there are
        //      potentially a bunch of requests, we'd have to be careful not to use up all the
        //      http connections that the browser allows.)
        // 2. Doing all that work at once can lock up the UI. ee https://trello.com/c/5yQMSVoI
        // This is also an issue in updateOutdatedStreams

        try {
            // oxlint-disable-next-line no-await-in-loop
            await fetchAndStoreStream({
                id,
                injector,
                storedStreamCount: i + 1,
                totalCount: streamIdsToFetch.length,
                executionId,
            });
            failedAttemptsByStreamId.delete(id);
        } catch (err) {
            // One unreachable asset says nothing about the other streams, so it must not end the
            // pass. Storage, network, and database errors are about the device, so they still
            // bubble up: every stream behind this one would fail the same way.
            if ((err as Error).constructor !== StorableImageError) throw err;

            recordFailedAttempt({ id, err: err as StorableImageError, injector, executionId });
        }
        logInDevMode(injector, `${i + 1} of ${streamIdsToFetch.length} streams stored`);
        if (abortLoading) break;
    }
    return !abortLoading;
}

async function fetchAndStoreStream({
    id,
    injector,
    storedStreamCount,
    totalCount,
    executionId,
}: {
    id: string;
    injector: angular.auto.IInjectorService;
    storedStreamCount: number;
    totalCount: number;
    executionId: string;
}) {
    await throwIfNotEnoughStorageAvailable();
    const TimerSingleton = injector.get<TimerSingleton>('timerSingleton');
    const timerKey = `offline_mode:stored_stream:${id}`;
    TimerSingleton.startTimer(timerKey);

    const stream = await getStreamAndEnsureStored({
        id,
        ensureLessonsAndImagesStored: true,
    });
    if (stream) {
        TimerSingleton.finishTimer(timerKey, 'offline_mode:stored_stream', {
            lesson_stream_id: stream.id,
            total: totalCount,
            value: totalCount - storedStreamCount,
            params: {
                execution_id: executionId,
            },
        });
    }
    return stream;
}

// NOTE: currentUserLocale has to be the current user's locale,
// because the api will use in_users_locale_or_en
export default function ensureAllContentStoredForStreams(
    streamLocalePackIds: string[],
    currentUser: CurrentUserIguanaObject,
    injector: ng.auto.IInjectorService,
): { promise: Promise<boolean>; abort: () => Promise<boolean> } {
    let abortCallback;
    const executionId = generateGuid();

    // This promise is going to resolve in one of two cases:
    // 1. Loading is complete
    // 2. Loading was aborted and all in-flight calls are complete
    let loadingCompleteOrAbortedPromise: Promise<boolean>;

    const abortLoadingPromise = new Promise<void>(res => {
        abortCallback = () => {
            const EventLogger = injector.get<EventLogger>('EventLogger');
            EventLogger.log('offline_mode:abort_loading_streams', { label: 'executionId' });
            res();
            // We want abort to return a promise that resolves only after the
            // loading has been successfully stopped
            return loadingCompleteOrAbortedPromise;
        };
    });

    loadingCompleteOrAbortedPromise = execute(
        streamLocalePackIds,
        currentUser,
        injector,
        executionId,
        abortLoadingPromise,
    );

    return { promise: loadingCompleteOrAbortedPromise, abort: abortCallback! };
}
